import { autorun, makeAutoObservable } from 'mobx'

import { IntegrationAuthClient } from '@/store/integrations'
import { ISpotifyErrorResponse, ISpotifyTokenResponse } from '@/store/integrations/spotify/types'
import { applyProperties } from '@/store/utils'
import { apiUrl } from '@/utils/constants'
import { generateRandomString } from '@/utils/generate-random-string'
import { isNonNullish } from '@/utils/type-guards'

import { generateCodeChallenge, generateCodeVerifier } from './pkce'
import { OAUTH_KEYS, REACTIVE_KEYS, getItem, removeItem, setItem } from './storage'
import { FetchMethod, FirebaseTokenResponse, SpotifyTokenError, isProperty } from './types'

const SPOTIFY_API_URL = 'https://api.spotify.com/v1'

export class SpotifyAuthClient implements IntegrationAuthClient {
  private isRefreshingToken = false
  private tokenRefreshPromise?: Promise<unknown>

  public accessToken?: string
  public refreshToken?: string

  public authRedirectUrl?: string

  public email?: string

  public isConnecting = false

  constructor() {
    makeAutoObservable(this)

    this.applyTokens()

    autorun(() => {
      Object.entries(REACTIVE_KEYS).forEach(([property, storageKey]) => {
        if (!isProperty(property, this)) return

        const value = this[property]

        if (isNonNullish(value)) {
          setItem(storageKey, value.toString())
        } else {
          removeItem(storageKey)
        }
      })
    })

    addEventListener('storage', event => {
      // Only react to changes in reactive keys (not OAuth-only keys)
      const match = Object.entries(REACTIVE_KEYS).find(([, key]) => key === event.key)
      if (!match) return

      this.applyTokens()
    })
  }

  public get isAuthenticated(): boolean {
    return Boolean(this.accessToken)
  }

  applyTokens() {
    this.apply({
      accessToken: getItem(REACTIVE_KEYS.accessToken),
      authRedirectUrl: getItem(REACTIVE_KEYS.authRedirectUrl),
      email: getItem(REACTIVE_KEYS.email),
      refreshToken: getItem(REACTIVE_KEYS.refreshToken)
    })
  }

  public async connect(options?: { currentUrl?: string; showDialog?: boolean }): Promise<void> {
    if (this.isConnecting) {
      throw new Error('Spotify connection already in progress')
    }

    try {
      const { currentUrl, showDialog } = options ?? {}

      const state = generateRandomString(16)
      const codeVerifier = generateCodeVerifier()
      const codeChallenge = await generateCodeChallenge(codeVerifier)
      const authRedirectUrl = currentUrl !== '/login' ? currentUrl : ''

      this.apply({
        authRedirectUrl,
        isConnecting: true
      })

      this.saveOAuthKeys(state, codeVerifier, authRedirectUrl)

      const url = new URL(`${apiUrl}/authorize-spotify`)

      url.searchParams.append('state', state)
      url.searchParams.append('code_challenge', codeChallenge)
      url.searchParams.append('code_challenge_method', 'S256')
      if (showDialog) url.searchParams.append('showDialog', String(showDialog))

      window.location.href = url.toString()
    } catch (error) {
      this.apply({
        isConnecting: false
      })
      throw error
    }
  }

  public disconnect(): void {
    this.apply({
      accessToken: undefined,
      authRedirectUrl: undefined,
      email: undefined,
      isConnecting: false,
      refreshToken: undefined
    })

    this.clearOAuthKeys()
  }

  private apply(values: Partial<SpotifyAuthClient>) {
    applyProperties(this, values)
  }

  private saveOAuthKeys(state: string, codeVerifier: string, authRedirectUrl?: string): void {
    setItem(OAUTH_KEYS.state, state)
    setItem(OAUTH_KEYS.codeVerifier, codeVerifier)
    if (authRedirectUrl) {
      setItem(REACTIVE_KEYS.authRedirectUrl, authRedirectUrl)
    }
  }

  private clearOAuthKeys(): void {
    removeItem(OAUTH_KEYS.state)
    removeItem(OAUTH_KEYS.codeVerifier)
  }

  private storeToken(response: ISpotifyTokenResponse): void {
    const { access_token, profile, refresh_token } = response

    this.apply({
      accessToken: access_token,
      ...(refresh_token && { refreshToken: refresh_token }),
      email: profile?.email,
      isConnecting: false
    })
  }

  public async refreshAccessToken() {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true

      this.tokenRefreshPromise = this.getRefreshedToken()
        .then(response => {
          if (response.access_token) {
            this.storeToken(response)
          } else {
            this.disconnect()
            throw new Error('Could not refresh token. Disconnecting Spotify.')
          }
        })
        .catch(error => {
          throw error
        })
        .finally(() => {
          this.isRefreshingToken = false
        })
    }

    return this.tokenRefreshPromise
  }

  private async getRefreshedToken(): Promise<ISpotifyTokenResponse> {
    const url = new URL(`${apiUrl}/refresh-spotify-token`)
    url.searchParams.append('refresh_token', this.refreshToken || '')

    const response = await fetch(url.toString(), { credentials: 'include' })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Token refresh failed: ${errorText}`)
    }

    return response.json() as Promise<ISpotifyTokenResponse>
  }

  public async apiRequest<T>(
    endPoint: string,
    method: FetchMethod = FetchMethod.GET,
    body?: string
  ): Promise<T> {
    const sendRequest = async (): Promise<Response> => {
      if (!this.accessToken) throw new Error('Invalid Spotify access token')
      return await this.performFetchRequest(endPoint, method, body)
    }

    const response = await sendRequest()

    if (!response.ok) {
      const data = (await response.json()) as unknown

      if ((data as ISpotifyErrorResponse).error) {
        console.error((data as ISpotifyErrorResponse).error?.message)
        throw new Error((data as ISpotifyErrorResponse).error.message)
      } else {
        throw new Error(response.statusText)
      }
    }

    if (response.status !== 200 || method === FetchMethod.PUT) {
      return {} as T
    }

    return response.json() as Promise<T>
  }

  private async performFetchRequest(
    endPoint: string,
    method: FetchMethod,
    body?: string
  ): Promise<Response> {
    const response = await fetch(`${SPOTIFY_API_URL}${endPoint}`, {
      body,
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      },
      method
    })

    if (response.status === 401) {
      try {
        await this.refreshAccessToken()
        return this.performFetchRequest(endPoint, method, body)
      } catch (error) {
        this.disconnect()
        console.error(error)
        throw error
      }
    }

    return response
  }

  public async handleAuthCallback(): Promise<void | FirebaseTokenResponse> {
    // Ensure we have the latest values from localStorage before validation
    // This is critical because the instance state might be out of sync
    this.applyTokens()

    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const error = params.get('error')
    const stateParam = params.get('state')

    // Check for Spotify error parameter first
    if (error) {
      this.disconnect()
      throw new Error(`Spotify authentication error: ${error}. User may have denied access.`)
    }

    // Check for missing authorization code
    if (!code) {
      this.disconnect()
      throw new Error('Could not authenticate to Spotify: Missing authorization code in callback')
    }

    // Check for missing state parameter
    if (!stateParam) {
      this.disconnect()
      throw new Error('Could not authenticate to Spotify: Missing state parameter in callback')
    }

    // Read state directly from localStorage as source of truth
    // These are OAuth-only values, not instance properties
    const storedState = getItem(OAUTH_KEYS.state)
    const storedCodeVerifier = getItem(OAUTH_KEYS.codeVerifier)

    // Check for state mismatch with detailed logging
    if (stateParam !== storedState) {
      console.error('State mismatch:', {
        expected: storedState,
        received: stateParam
      })
      this.disconnect()
      throw new Error(
        'Could not authenticate to Spotify: State mismatch. This may occur if you opened the link in a different browser or cleared your browser data. Please try connecting again.'
      )
    }

    // Validate codeVerifier before token exchange - read directly from localStorage
    if (!storedCodeVerifier) {
      this.disconnect()
      throw new Error(
        'Could not authenticate to Spotify: Missing code verifier. This may occur if your browser data was cleared. Please try connecting again.'
      )
    }

    const response = await this.getToken(code, stateParam, storedCodeVerifier)

    if (response.error) {
      this.disconnect()
      throw new SpotifyTokenError(response.error, response.email)
    }

    if (response.hasMigrated && response.user) {
      this.clearOAuthKeys()
      return response
    }

    this.clearOAuthKeys()

    this.storeToken(response.spotify)
  }

  private async getToken(
    code: string,
    state: string,
    codeVerifier: string
  ): Promise<FirebaseTokenResponse> {
    const url = new URL(`${apiUrl}/spotify-token`)
    url.searchParams.append('code', code)
    url.searchParams.append('state', state)
    url.searchParams.append('code_verifier', codeVerifier)

    const response = await fetch(url.toString(), { credentials: 'include' })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Token exchange failed: ${errorText}`)
    }

    return response.json() as Promise<FirebaseTokenResponse>
  }
}
